import { Modal, ModalBody } from "flowbite-react"
import { CheckCircle, Phone, ArrowRight, ArrowLeft, Gift, Heart, User } from "iconoir-react"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { VibratoClientContext, UserContext } from "../contexts"
import { LoadingSpinner } from "../../LoadingSpinner"
import ReferralModal from "../ReferralModal"
import GiftSubscriptionModal from "../GiftSubscriptionModal"

function handleSubmit(setError: any, setSubmitted: any, feedback: any, call: any, client: any, setIsSubmitting: any) {
  return function fn(event: any) {
    event.preventDefault()

    setError(null)
    setIsSubmitting(true)

    const feedbackOrNull = feedback.length > 0 ? feedback : null

    let starsOrNull = null
    document.querySelectorAll('input[name="hs-ratings-readonly"]:checked').forEach((star: any) => {
      starsOrNull = parseInt(star.value)
    })

    if (starsOrNull === null && feedbackOrNull === null) {
      setError("Please provide a rating or feedback.")
      setIsSubmitting(false)
      return
    }

    client
      .createCallFeedback(call.id, feedbackOrNull, starsOrNull)
      .then((data: any) => {
        console.log("Submitted call feedback", data)
        setSubmitted(true)
      })
      .catch((err: any) => {
        setError(`Error submitting feedback${err}`)
        setSubmitted(false)
      })
      .finally(() => {
        setIsSubmitting(false)
      })

    return starsOrNull
  }
}

// Redesigned FeedbackForm component
function FeedbackForm({
  feedback,
  setFeedback,
  error,
  submitted,
  isSubmitting = false
}: {
  feedback: string
  setFeedback: (feedback: string) => void
  error: string | null
  submitted: boolean
  isSubmitting?: boolean
}) {
  const handleFeedbackChange = (event: any) => {
    setFeedback(event.target.value)
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
        <div className="space-y-6">
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4" htmlFor="stars">
              Rate your call:
            </label>
            <StarRating />
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-3" htmlFor="feedback">
              Tell us more (optional):
            </label>
            <textarea
              className="w-full h-32 border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all resize-none"
              id="feedback"
              name="feedback"
              value={feedback}
              onChange={handleFeedbackChange}
              placeholder="Tell us about your experience..."
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {submitted && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-sm text-green-700 font-medium">Thank you for your feedback! 🙏</p>
        </div>
      )}
    </div>
  )
}

export default function RateCallModal({
  call,
  callService,
  onClose
}: {
  call: any
  callService: any
  onClose?: () => void
}): React.JSX.Element {
  const client = useContext(VibratoClientContext)
  const user = useContext(UserContext)
  const navigate = useNavigate()

  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [modalStep, setModalStep] = useState<"summary" | "feedback" | "recommend">("summary")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showReferralModal, setShowReferralModal] = useState(false)
  const [showGiftModal, setShowGiftModal] = useState(false)

  const localStorageKey = `rate_call_modal:${call.id}`
  const isSuccessful = callService.getFrontendStatus() === "successful"

  // Show recommend step if: call successful, high rating (4-5 stars), or accessed via URL
  // const shouldShowRecommendStep = isSuccessful || (starRating && starRating >= 4) || accessedViaUrl

  // Helper function to get current star rating from form
  // const getCurrentStarRating = () => {
  //   let currentStarRating = null
  //   document.querySelectorAll('input[name="hs-ratings-readonly"]:checked').forEach((star: any) => {
  //     currentStarRating = parseInt(star.value)
  //   })
  //   return currentStarRating
  // }

  // Check if we should show recommend step based on current form state
  // const shouldShowRecommendStepCurrent = () => {
  //   const currentStarRating = getCurrentStarRating()
  //   return isSuccessful || (currentStarRating && currentStarRating >= 4) || accessedViaUrl
  // }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const showModalParam = urlParams.get("show_modal")
    const showRecommendParam = urlParams.get("show_recommend")

    if (showModalParam === "1") {
      setShowModal(true)
      return
    }

    if (showRecommendParam === "1") {
      setShowModal(true)
      setModalStep("recommend")
      return
    }

    if (call.feedback?.length > 0) {
      setShowModal(false)
      return
    }

    if (window.localStorage.getItem(localStorageKey) === undefined) {
      setShowModal(true)
    } else if (window.localStorage.getItem(localStorageKey) === "false") {
      setShowModal(false)
    } else {
      setShowModal(true)
    }
  }, [call, setShowModal, callService, localStorageKey])

  const submit = handleSubmit(setError, setSubmitted, feedback, call, client, setIsSubmitting)

  const handleClose = () => {
    setShowModal(false)
    window.localStorage.setItem(localStorageKey, "false")
    if (onClose) onClose()
  }

  const handleFeedbackSubmit = async (event: any) => {
    await submit(event)

    // Always show recommend step after feedback submission
    setModalStep("recommend")
  }

  const goToFeedback = () => {
    setModalStep("feedback")
  }

  const goToRecommend = () => {
    setModalStep("recommend")
  }

  const retryCall = useCallback(async () => {
    console.log("retrying call", call.id)
    setError(null)
    setRetryError(null)

    try {
      const newCall = await client.createRetryCall(call.id)
      console.log("created new call", newCall)
      navigate(`/calls/${newCall.id}/edit`)
    } catch (err: any) {
      console.error("error creating new call", err)
      const errorMessage = err.data.join(", ") || "Failed to create retry call. Please try again."
      setRetryError(errorMessage)
    }
  }, [call, client, navigate])

  const handleReferralClick = () => {
    setShowReferralModal(true)
  }

  const handleGiftClick = () => {
    setShowGiftModal(true)
  }

  return (
    <Modal show={showModal} onClose={handleClose} size="xl" className="pt-safe" dismissible>
      <ModalBody className="p-0">
        <div className="flex flex-col">
          {/* Header Section with Gradient Background */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-center mb-3">
              <div className="bg-white/20 p-2 rounded-full">
                {isSuccessful ? (
                  <CheckCircle className="w-6 h-6" />
                ) : callService.hasMissingData() ? (
                  <Phone className="w-6 h-6" />
                ) : (
                  <Phone className="w-6 h-6" />
                )}
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center">
              {modalStep === "summary" && isSuccessful && "Call Completed Successfully! 🎉"}
              {modalStep === "summary" && callService.hasMissingData() && "We're Almost There! 😅"}
              {modalStep === "summary" && !isSuccessful && !callService.hasMissingData() && "Let's Try Again 💪"}
              {modalStep === "feedback" && "How Was Your Call?"}
              {modalStep === "recommend" && "Like Vibrato? Tell a Friend!"}
            </h2>
            <p className="text-center text-white/80 mt-1">
              {modalStep === "summary" && "Your call details and next steps"}
              {modalStep === "feedback" && "Help us improve Vibrato"}
              {modalStep === "recommend" && "Share Vibrato with friends and family"}
            </p>
          </div>

          <div className="p-6">
            {modalStep === "summary" && (
              <div className="space-y-6">
                {/* Call Summary Card */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Call Summary</h3>
                  <p className="text-gray-700 leading-relaxed">{call.summary}</p>
                </div>

                {/* Action Message */}
                {isSuccessful && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <p className="text-green-800 font-medium text-center">
                      Great job! Your call was completed successfully.
                    </p>
                  </div>
                )}

                {callService.hasMissingData() && (
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
                    <p className="text-orange-800 mb-4">
                      We hit a snag on our first call attempt – it seems we're missing some key details. Click 'Retry
                      Call', and on the next screen, you'll have the chance to fill in the gaps.
                    </p>
                    <div className="bg-white/50 p-3 rounded-lg border border-orange-100 mb-4">
                      <p className="text-orange-700 font-medium text-sm">
                        💡 Fun fact: over 90% of calls succeed after a retry!
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={retryCall}
                        className="w-fit bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2"
                      >
                        <Phone className="w-5 h-5" />
                        <span>Retry Call</span>
                      </button>
                    </div>
                  </div>
                )}

                {!isSuccessful && !callService.hasMissingData() && (
                  <div className="flex flex-col gap-4 items-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
                    <p className="text-blue-800">
                      Sometimes, external factors can influence the outcome. Let's give it another shot – we're here to
                      help you succeed!
                    </p>

                    <button
                      onClick={retryCall}
                      className="w-fit bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Retry Call</span>
                    </button>
                  </div>
                )}

                {retryError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm text-red-700">{retryError}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                  <button
                    onClick={goToFeedback}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2"
                  >
                    <span>Rate This Call</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>

                  {/* Close Button */}
                  <button
                    onClick={handleClose}
                    className="w-full bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {modalStep === "feedback" && (
              <div className="space-y-6">
                <FeedbackForm
                  feedback={feedback}
                  setFeedback={setFeedback}
                  error={error}
                  submitted={submitted}
                  isSubmitting={isSubmitting}
                />

                {/* Navigation Buttons */}
                <div className="flex flex-col gap-3">
                  {!submitted ? (
                    <>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setModalStep("summary")}
                          type="button"
                          disabled={isSubmitting}
                          className="flex-1 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 font-semibold py-3 px-5 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <ArrowLeft className="w-5 h-5" />
                          <span>Back</span>
                        </button>

                        <button
                          onClick={handleFeedbackSubmit}
                          disabled={isSubmitting}
                          className="flex-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {isSubmitting && <LoadingSpinner className="w-5 h-5" />}
                          <span>{isSubmitting ? "Submitting..." : "Submit Feedback"}</span>
                        </button>
                      </div>

                      {/* Close Button */}
                      <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                      >
                        Close
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={goToRecommend}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2"
                    >
                      <span>Continue</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {modalStep === "recommend" && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Like Vibrato? Tell a Friend!</h3>
                    <p className="text-gray-600">Help others discover the magic of AI-powered calling</p>
                  </div>

                  {user.plan === "freemium" ? (
                    <div
                      onClick={handleReferralClick}
                      className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white p-6 rounded-xl cursor-pointer transition-all transform hover:scale-[1.02] shadow-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-white/20 p-3 rounded-full">
                            <User className="w-6 h-6" />
                          </div>
                          <div className="text-left">
                            <h4 className="font-semibold text-lg">Refer Friends, Get Free Calls</h4>
                            <p className="text-emerald-100 text-sm">You both get 60 free minutes when they sign up.</p>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-white/80" />
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={handleGiftClick}
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white p-6 rounded-xl cursor-pointer transition-all transform hover:scale-[1.02] shadow-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-white/20 p-3 rounded-full">
                            <Gift className="w-6 h-6" />
                          </div>
                          <div className="text-left">
                            <h4 className="font-semibold text-lg">Give a Free Month of Plus</h4>
                            <p className="text-purple-100 text-sm">
                              Let your friends try our best features for 30 days, on you.
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-white/80" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation Button */}
                <div className="w-full flex justify-center">
                  <button
                    onClick={handleClose}
                    className="w-full bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 font-semibold py-3 px-8 rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalBody>

      {/* Referral and Gift Modals */}
      <ReferralModal show={showReferralModal} onClose={() => setShowReferralModal(false)} />
      <GiftSubscriptionModal show={showGiftModal} onClose={() => setShowGiftModal(false)} />
    </Modal>
  )
}

export function RateCall({ call }: { call: any; callService: any }): React.JSX.Element {
  const client = useContext(VibratoClientContext)

  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [showComponent, setShowComponent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (call.feedback?.length > 0) {
      setShowComponent(false)
      return
    }

    // Show component for all call states
    setShowComponent(true)
  }, [call, setShowComponent])

  handleSubmit(setError, setSubmitted, feedback, call, client, setIsSubmitting)

  if (showComponent)
    return (
      <>
        {/* Horizontal divider */}
        <hr className="my-6 border-gray-200" />

        {/* Feedback title */}
        <div className="flex flex-wrap justify-center items-center gap-2">
          <div className="tracking-wide font-medium text-lg">Share Your Feedback</div>
        </div>
        <div className="w-full p-5 bg-white">
          <FeedbackForm
            feedback={feedback}
            setFeedback={setFeedback}
            error={error}
            submitted={submitted}
            isSubmitting={isSubmitting}
          />
        </div>
      </>
    )

  return <></>
}

function StarRating() {
  const stars = [5, 4, 3, 2, 1]

  return (
    <div className="flex flex-row-reverse justify-center items-center bg-white p-2 xl:p-4 rounded-xl border border-gray-200 w-fit mx-auto">
      {stars.map((value) => (
        <React.Fragment key={value}>
          <input
            id={`hs-ratings-readonly-${value}`}
            type="radio"
            className="peer -ms-3 xl:-ms-5 w-6 h-6 xl:w-8 xl:h-8 bg-transparent border-0 text-transparent cursor-pointer appearance-none checked:bg-none focus:bg-none focus:ring-0 focus:ring-offset-0"
            name="hs-ratings-readonly"
            value={value}
          />
          <label
            htmlFor={`hs-ratings-readonly-${value}`}
            className="peer-checked:text-yellow-400 text-gray-300 pointer-events-none dark:peer-checked:text-yellow-600 dark:text-gray-600 transition-colors hover:text-yellow-300"
          >
            <svg
              className="shrink-0 w-6 h-6 xl:w-8 xl:h-8"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z" />
            </svg>
          </label>
        </React.Fragment>
      ))}
    </div>
  )
}
